import { ref, onMounted, onUnmounted } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'mcf-theme-mode';
const themeMode = ref<ThemeMode>('light');
const isDark = ref<boolean>(false);
const listeners: Array<(dark: boolean) => void> = [];

function applyTheme(dark: boolean): void {
  isDark.value = dark;
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
  listeners.forEach((fn) => fn(dark));
}

function resolveAuto(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function getStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'auto') return stored;
  return 'light';
}

let mediaQuery: MediaQueryList | null = null;
function onMediaChange(): void {
  if (themeMode.value === 'auto') applyTheme(resolveAuto());
}

export function useTheme() {
  onMounted(() => {
    themeMode.value = getStoredMode();
    if (themeMode.value === 'auto') {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', onMediaChange);
      applyTheme(resolveAuto());
    } else {
      applyTheme(themeMode.value === 'dark');
    }
  });

  onUnmounted(() => {
    if (mediaQuery) mediaQuery.removeEventListener('change', onMediaChange);
  });

  function setMode(mode: ThemeMode): void {
    themeMode.value = mode;
    localStorage.setItem(STORAGE_KEY, mode);
    if (mode === 'auto') {
      if (!mediaQuery) {
        mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', onMediaChange);
      }
      applyTheme(resolveAuto());
    } else {
      if (mediaQuery) {
        mediaQuery.removeEventListener('change', onMediaChange);
        mediaQuery = null;
      }
      applyTheme(mode === 'dark');
    }
  }

  function toggle(): void {
    setMode(isDark.value ? 'light' : 'dark');
  }

  function onThemeChange(fn: (dark: boolean) => void): () => void {
    listeners.push(fn);
    return () => {
      const idx = listeners.indexOf(fn);
      if (idx >= 0) listeners.splice(idx, 1);
    };
  }

  return {
    themeMode,
    isDark,
    setMode,
    toggle,
    onThemeChange,
  };
}